import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ModalController, IonicModule } from '@ionic/angular';
import { AdminApiService, AdminTenant } from '../../services/admin-api.service';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Admin-only modal: lists every tenant with a search box. Selecting one dismisses
 * with the chosen tenant (role 'select'); the caller persists it as the active
 * "act-as" tenant. See ActiveTenantService / AuthMiddleware X-Active-Tenant-Id.
 */
@Component({
    selector: 'app-tenant-switcher-modal',
    templateUrl: './tenant-switcher-modal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        FormsModule,
        TranslatePipe,
    ],
})
export class TenantSwitcherModalComponent implements OnInit {
  /** Currently active tenant id, to highlight it in the list. A plain property on purpose:
   *  Ionic's ModalController assigns componentProps directly onto the instance, which
   *  would overwrite a signal input and break `activeId()` calls. */
  activeId: string | null = null;

  tenants: AdminTenant[] = [];
  filtered: AdminTenant[] = [];
  search = '';
  loading = true;
  error = false;

  constructor(
    private modalCtrl: ModalController,
    private adminApi: AdminApiService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.adminApi.getTenants().subscribe({
      next: tenants => {
        this.tenants = [...(tenants ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
        this.applyFilter();
        this.loading = false;
        // Async data resolved inside the Ionic modal's CD cycle, so reconcile explicitly
        // to avoid NG0100 (ExpressionChangedAfterItHasBeenChecked) on the loading *ngIf.
        this.cdr.markForCheck();
      },
      error: () => {
        this.error = true;
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  applyFilter(): void {
    const q = this.search.trim().toLowerCase();
    this.filtered = !q
      ? this.tenants
      : this.tenants.filter(t =>
          (t.name ?? '').toLowerCase().includes(q) ||
          (t.tenantCode ?? '').toLowerCase().includes(q) ||
          (t.regionCode ?? '').toLowerCase().includes(q));
  }

  select(tenant: AdminTenant): void {
    this.modalCtrl.dismiss({ id: tenant.id, name: tenant.name }, 'select');
  }

  dismiss(): void {
    this.modalCtrl.dismiss(null, 'cancel');
  }
}
